import { useEffect, useRef, useState } from 'react'
import { Palette, Paperclip, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useCreateDesignRequest, useGetMarketingRefs } from '../../hooks/backend/marketing'
import { useMarketingUpload, MAX_UPLOAD_BYTES } from '../../hooks/useMarketingUpload'
import { writeErrorMessage } from '../../utils/writeError'
import { fileSize, type MarketingRefs } from '../../utils/marketing'

/**
 * Ask the designer for something.
 *
 * REACHABLE FROM EVERY PAGE, not only from Marketing. That is the point of it:
 * the request this replaces is a WhatsApp message sent from wherever the person
 * happened to be standing, and a form you have to navigate to first is a form
 * that loses to WhatsApp every time. The button lives in the sidebar; this is
 * the dialog behind it.
 *
 * The brief is required and the dialog says why. A request with no brief moves
 * the thinking onto the designer, who cannot do it, so it comes back as a
 * question and the whole round trip was wasted. The database refuses it as well.
 *
 * FILES ARE CHOSEN NOW AND UPLOADED AFTER THE REQUEST EXISTS. An upload is
 * attached to a design request id, and there is no id until the request has been
 * written, so the two cannot be one call. The order is deliberate: the request is
 * created first and the files follow it. If a file then fails, the request is
 * still on the designer's board and the person is told exactly which file did not
 * arrive and where to add it. The other order, holding the request back until
 * every byte has landed, loses the whole request to one bad photograph.
 */
export function DesignRequestDialog({
  open,
  onOpenChange,
  contentItemId = null,
  contentItemTitle = null,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  contentItemId?: string | null
  contentItemTitle?: string | null
  onCreated?: () => void
}) {
  const refsQuery = useGetMarketingRefs()
  const create = useCreateDesignRequest()
  const { upload } = useMarketingUpload()

  const [title, setTitle] = useState('')
  const [brief, setBrief] = useState('')
  const [channel, setChannel] = useState('')
  const [format, setFormat] = useState('')
  const [dimensions, setDimensions] = useState('')
  const [references, setReferences] = useState('')
  const [neededBy, setNeededBy] = useState('')
  const [priority, setPriority] = useState('Medium')
  const [assignedTo, setAssignedTo] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const refsTrigger = refsQuery.trigger
  useEffect(() => {
    if (open) void refsTrigger({})
  }, [open, refsTrigger])

  // Reset on every open, not on close. A dialog that keeps last time's brief is
  // a dialog that sends last time's brief, and one that keeps last time's files
  // is worse: nobody re-reads a list they have already seen.
  useEffect(() => {
    if (!open) return
    setTitle('')
    setBrief('')
    setChannel('')
    setFormat('')
    setDimensions('')
    setReferences('')
    setNeededBy('')
    setPriority('Medium')
    setAssignedTo('')
    setFiles([])
    if (fileRef.current) fileRef.current.value = ''
  }, [open])

  const refs = (refsQuery.data as MarketingRefs | null | undefined) ?? null
  const designers = (refs?.people ?? []).filter((p) => p.is_designer)

  // Only asked when there is a real choice. One designer means no question, and
  // a dropdown with one option in it is a question with one answer.
  const mustChooseDesigner = designers.length > 1

  function addFiles(chosen: FileList | null) {
    if (!chosen || chosen.length === 0) return
    const picked = Array.from(chosen)
    const tooBig = picked.filter((f) => f.size > MAX_UPLOAD_BYTES)
    if (tooBig.length > 0) {
      // Said now rather than on submit. Discovering a file was too big after the
      // request has already gone means the designer has a request with a hole in
      // it and the person has to go and find where to fix it.
      toast.error('That file is too large', {
        description: `${tooBig.map((f) => f.name).join(', ')} is over 200 MB. Compress it or send a link to it instead.`,
      })
    }
    const room = picked.filter((f) => f.size <= MAX_UPLOAD_BYTES)
    setFiles((existing) => {
      const seen = new Set(existing.map((f) => `${f.name}:${f.size}`))
      return [...existing, ...room.filter((f) => !seen.has(`${f.name}:${f.size}`))]
    })
    if (fileRef.current) fileRef.current.value = ''
  }

  async function submit() {
    setBusy(true)
    try {
      const row = (await create.trigger({
        title,
        brief,
        channel: channel || null,
        format: format || null,
        dimensions: dimensions || null,
        reference_urls: references
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter((s) => s !== ''),
        needed_by: neededBy || null,
        priority,
        content_item_id: contentItemId,
        assigned_to: mustChooseDesigner ? assignedTo || null : null,
      }).result) as { id?: string } | null

      const requestId = row?.id ?? null

      if (files.length > 0 && requestId === null) {
        // The request was written and the handler did not hand back the row, so
        // there is nothing to attach the files to. Said plainly rather than
        // swallowed: the person has files in their hand and needs to know they
        // are not on the request.
        toast.warning('Design request sent, without the files', {
          description:
            'The request is on the designer’s board. The files could not be attached because the request did not come back with an id. Open the design queue and add them there.',
        })
      } else if (files.length > 0) {
        const { done, failed } = await upload(files, {
          designRequestId: requestId,
          kind: 'Reference',
        })
        if (failed.length > 0) {
          toast.error(
            done === 0
              ? 'Design request sent, no files attached'
              : `Design request sent, ${done} of ${files.length} files attached`,
            {
              description: `${failed.join(' | ')} You can add the missing file from the design queue.`,
            },
          )
        } else {
          toast.success('Design request sent', {
            description: `It is on the designer’s task board with ${
              done === 1 ? 'your file' : `your ${done} files`
            } attached.`,
          })
        }
      } else {
        toast.success('Design request sent', {
          description: 'It is on the designer’s task board now.',
        })
      }

      onOpenChange(false)
      onCreated?.()
    } catch (err) {
      toast.error('The request was not created', { description: writeErrorMessage(err) })
    } finally {
      setBusy(false)
    }
  }

  const ready =
    title.trim() !== '' && brief.trim() !== '' && (!mustChooseDesigner || assignedTo !== '')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Ask for a design
          </DialogTitle>
          <DialogDescription>
            {designers.length === 0
              ? 'Nobody is set up as the designer yet, so this cannot be sent. Ask an admin to set it.'
              : contentItemTitle
                ? `This will be linked to "${contentItemTitle}" and will appear on the designer’s task board.`
                : 'This becomes a task on the designer’s board, with your name on it as the person who asked.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="dr-title" className="text-xs">
              What do you need, in a few words
            </Label>
            <Input
              id="dr-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="A2 poster for the Baragwanath rank"
              className="h-9"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="dr-brief" className="text-xs">
              The brief
            </Label>
            <Textarea
              id="dr-brief"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              rows={4}
              placeholder="Who it is for, what it has to say, where it will be seen, and anything that must appear on it."
              className="text-sm"
            />
            <p className="text-[11px] text-muted-foreground">
              Required. A request with no brief comes back as a question, which costs the designer a
              day and you a day.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Where it goes</Label>
              <NativeSelect
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                className="h-9"
              >
                <option value="">Not sure yet</option>
                {(refs?.channels ?? []).map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">What kind of thing</Label>
              <NativeSelect
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                className="h-9"
              >
                <option value="">Not sure yet</option>
                {(refs?.formats ?? []).map((f) => (
                  <option key={f.code} value={f.code}>
                    {f.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dr-dim" className="text-xs">
                Size
              </Label>
              {/* Free text on purpose. "1080x1350", "A4 landscape" and "the width
                  of the bakkie door" are all real answers and none is an enum. */}
              <Input
                id="dr-dim"
                value={dimensions}
                onChange={(e) => setDimensions(e.target.value)}
                placeholder="A2 portrait, or 1080x1350"
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dr-when" className="text-xs">
                Needed by
              </Label>
              <Input
                id="dr-when"
                type="date"
                value={neededBy}
                onChange={(e) => setNeededBy(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">How urgent</Label>
              <NativeSelect
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="h-9"
              >
                {/* High, Medium, Low. The SAME three words the task board uses,
                    because a design request BECOMES a task: task_create_v1 puts
                    this string straight into task.priority, whose check accepts
                    exactly these three. "Normal" was here first and every design
                    request created with it was refused by the database. One
                    vocabulary, one place it is allowed to change. */}
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </NativeSelect>
            </div>
            {mustChooseDesigner && (
              <div className="space-y-1.5">
                <Label className="text-xs">Which designer</Label>
                <NativeSelect
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  className="h-9"
                >
                  <option value="">Choose</option>
                  {designers.map((d) => (
                    <option key={d.email} value={d.email}>
                      {d.name}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="dr-refs" className="text-xs">
              Anything to look at
            </Label>
            <Input
              id="dr-refs"
              value={references}
              onChange={(e) => setReferences(e.target.value)}
              placeholder="Links, one after another"
              className="h-9"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Anything to send in</Label>
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 gap-1.5"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
              >
                <Paperclip className="h-3.5 w-3.5" />
                Attach a file
              </Button>
              <span className="text-[11px] text-muted-foreground">
                A photograph, a PDF, a logo, the document it has to quote. Up to 200 MB each.
              </span>
            </div>
            {files.length > 0 && (
              <ul className="space-y-1 pt-1">
                {files.map((f) => (
                  <li
                    key={`${f.name}:${f.size}`}
                    className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-2 py-1"
                  >
                    <span className="min-w-0 flex-1 truncate text-[11px] text-foreground">
                      {f.name}
                    </span>
                    <span className="shrink-0 text-[11px] text-muted-foreground">
                      {fileSize(f.size) ?? ''}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 shrink-0 p-0"
                      disabled={busy}
                      aria-label={`Remove ${f.name}`}
                      onClick={() =>
                        setFiles((existing) =>
                          existing.filter((x) => !(x.name === f.name && x.size === f.size)),
                        )
                      }
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-muted-foreground">
              These go to the designer as what you sent in, not as the artwork. They are uploaded
              once the request has been created, so it is the request that reaches the board first.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="brand"
            size="sm"
            className="gap-1.5"
            disabled={!ready || busy || designers.length === 0}
            onClick={() => void submit()}
          >
            {busy && <Spinner className="h-3.5 w-3.5" />}
            Send it to the designer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
